import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import "../Css/login.css";
import BASEURL from '../API/Config';
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiAlertCircle } from 'react-icons/fi';


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Clear previous session data
  localStorage.clear();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = {};
    if (!email) validationErrors.email = 'Email is required';
    if (!password) validationErrors.password = 'Password is required';

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await fetch(`${BASEURL}/user/admin-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        const fullName = `${data.firstname || ''} ${data.lastname || ''}`.trim();
        localStorage.setItem('UserName', fullName || data.Username || 'Admin');
        localStorage.setItem('UserEmail', data.email || '');
        localStorage.setItem('UserFirstName', data.firstname || '');
        localStorage.setItem('UserLastName', data.lastname || '');
        localStorage.setItem('Role', data.role || 'Administrator');
        
        // Success animation or delay could be added here
        navigate('/admin');
      } else {
        const errorData = await response.json();
        setErrors({ form: errorData.message || 'Invalid credentials' });
        Swal.fire({
          icon: 'error',
          title: 'Login Failed',
          text: errorData.message || 'Please check your credentials and try again.',
          background: '#1e293b',
          color: '#f8fafc',
          confirmButtonColor: '#10b981',
        });
      }
    } catch (error) {
      setErrors({ form: 'Connection error. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className='login-container'>
      <motion.div 
        className='login-box'
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="brand-section" variants={itemVariants}>
          <div className="brand-logo">S</div>
          <h3>Welcome Back</h3>
          <p>Login to your Shakambari admin account</p>
        </motion.div>
        
        <form onSubmit={handleSubmit}>
          <motion.div className="form-group-wrapper" variants={itemVariants}>
            <label className="input-label">Email Address</label>
            <div className="input-container">
              <FiMail className="input-icon" />
              <input
                type="email"
                className="custom-input"
                placeholder="admin@shakambari.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <AnimatePresence>
              {errors.email && (
                <motion.p 
                  className="error-message"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiAlertCircle /> {errors.email}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div className="form-group-wrapper" variants={itemVariants}>
            <label className="input-label">Password</label>
            <div className="input-container">
              <FiLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                className="custom-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            <AnimatePresence>
              {errors.password && (
                <motion.p 
                  className="error-message"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <FiAlertCircle /> {errors.password}
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div variants={itemVariants}>
            <button className='login-button' type='submit' disabled={loading}>
              {loading ? (
                <div className="loader"></div>
              ) : (
                <>
                  Sign In <FiArrowRight />
                </>
              )}
            </button>
          </motion.div>

          <motion.div className="footer-links" variants={itemVariants}>
            <label className="remember-me">
              <input type="checkbox" />
              <span>Remember me</span>
            </label>
            <Link to="/forget-password" title="Forget-password" className='forget-link'>
              Forgot password?
            </Link>
          </motion.div>

          <AnimatePresence>
            {errors.form && (
              <motion.div 
                className="error-message" 
                style={{ marginTop: '16px', justifyContent: 'center' }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <FiAlertCircle /> {errors.form}
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </motion.div>
    </div>
  );
};

export default Login;
